import React, { useMemo } from 'react';
import moment from 'moment';
import { Box, Typography, Skeleton, alpha, Paper, Chip } from '@mui/material';
import { FlightLand, MeetingRoom } from '@mui/icons-material';
import DateHeader from './DateHeader';
import { generateDateRange, getTodayDateString, CALENDAR_GRID_DAY_COUNT } from '../../utils/dateUtils';
import { DASHBOARD_ACCENT } from '../../utils/dashboardSurfaceSx';
import { mobileAppGlassCard } from '../layout/mobile/mobileAppTheme';
import { LOGIN_ACCENT } from '../auth/loginDarkTheme';
import { getUltraRoomGridContainerSx } from '../../utils/dashboardUltraGridLayout';

const WEEKDAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MOBILE_ROOM_LABEL_WIDTH = 44;
const MOBILE_ULTRA_GRID_COLS = 3;

const STATS_CHIP_H = 26;

function ultraStatChipLabel(isDark, countWidth, textSuffix) {
  return (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.45 }}>
      <Skeleton
        variant="rounded"
        animation="wave"
        width={countWidth}
        height={14}
        sx={{ bgcolor: isDark ? alpha('#fff', 0.12) : alpha('#000', 0.08) }}
      />
      <Box component="span" sx={{ fontWeight: 600, fontSize: '0.72rem', color: 'inherit' }}>
        {textSuffix}
      </Box>
    </Box>
  );
}

/** Persisted after a successful calendar load so skeleton row count matches the hotel. */
export const BOOKING_CALENDAR_ROOM_ROWS_STORAGE_KEY = 'roomnexa:dashboard:calendar-room-rows:v1';

export function readStoredCalendarRoomRowCount(fallback = 6) {
  if (typeof window === 'undefined') return fallback;
  try {
    const raw = window.sessionStorage.getItem(BOOKING_CALENDAR_ROOM_ROWS_STORAGE_KEY);
    const n = parseInt(raw, 10);
    if (Number.isInteger(n) && n > 0 && n < 500) return n;
  } catch {
    // ignore
  }
  return fallback;
}

function buildMonthWeeks(anchorDate) {
  const m = moment(anchorDate).startOf('day');
  const monthStart = m.clone().startOf('month');
  const monthEnd = m.clone().endOf('month');
  const gridStart = monthStart.clone().startOf('week');
  const gridEnd = monthEnd.clone().endOf('week');
  const days = [];
  const cursor = gridStart.clone();
  while (cursor.isSameOrBefore(gridEnd, 'day')) {
    days.push(cursor.clone());
    cursor.add(1, 'day');
  }
  const weeks = [];
  for (let i = 0; i < days.length; i += 7) {
    weeks.push(days.slice(i, i + 7));
  }
  return {
    weeks,
    monthLabel: monthStart.format('MMMM YYYY'),
    anchorMonth: m.month(),
  };
}

function boneColor(isDark) {
  return isDark ? alpha('#fff', 0.12) : alpha('#000', 0.09);
}

function mobilePillSkeleton(isDark, width, height = 22) {
  return (
    <Skeleton
      variant="rounded"
      animation="wave"
      width={width}
      height={height}
      sx={{ borderRadius: 999, bgcolor: boneColor(isDark), flexShrink: 0 }}
    />
  );
}

/**
 * Month grid: same shell as `BookingCalendarMonthOverview` — real month title,
 * weekday headers, real day numbers; skeletons only for load %, arrivals, nights, bar.
 */
function MonthCalendarSkeleton({ theme, anchorDate }) {
  const isDark = theme === 'dark';
  const { weeks, monthLabel, anchorMonth } = useMemo(
    () => buildMonthWeeks(anchorDate || new Date()),
    [anchorDate]
  );
  const todayStr = moment().format('YYYY-MM-DD');

  const surfaceBorder = isDark ? alpha('#fff', 0.08) : alpha('#000', 0.08);
  const muted = isDark ? alpha('#fff', 0.45) : alpha('#000', 0.45);
  const headingBg = isDark ? alpha(DASHBOARD_ACCENT, 0.07) : alpha(DASHBOARD_ACCENT, 0.12);
  const defaultCellBg = (inMonth) =>
    isDark
      ? inMonth
        ? alpha('#fff', 0.03)
        : alpha('#fff', 0.01)
      : inMonth
        ? alpha('#fff', 0.85)
        : alpha('#000', 0.02);
  const dayNumColor = (inMonth) => (!inMonth ? muted : isDark ? '#fff' : '#111');
  const bc = boneColor(isDark);

  return (
    <Box
      className="booking-calendar-container"
      sx={{ width: '100%', px: { xs: 0.5, sm: 1 }, pb: 2 }}
      aria-busy="true"
      aria-label="Loading calendar"
    >
      <Box
        sx={{
          mb: 2,
          py: 1.5,
          px: 2,
          borderRadius: 2,
          border: `1px solid ${surfaceBorder}`,
          background: headingBg,
          display: 'flex',
          flexDirection: 'column',
          gap: 0.75,
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 700, letterSpacing: '0.02em' }}>
          {monthLabel}
        </Typography>
        <Skeleton
          variant="rounded"
          animation="wave"
          height={18}
          sx={{
            maxWidth: 720,
            width: '100%',
            bgcolor: bc,
          }}
        />
        <Skeleton
          variant="rounded"
          animation="wave"
          height={14}
          sx={{ maxWidth: 520, width: '85%', bgcolor: bc }}
        />
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
          gap: { xs: 0.5, sm: 1 },
          mb: 1,
        }}
      >
        {WEEKDAY_LABELS.map((wd) => (
          <Typography
            key={wd}
            variant="caption"
            sx={{
              textAlign: 'center',
              fontWeight: 700,
              color: muted,
              fontSize: '0.68rem',
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
            }}
          >
            {wd}
          </Typography>
        ))}
      </Box>

      {weeks.map((week, wi) => (
        <Box
          key={wi}
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
            gap: { xs: 0.5, sm: 1 },
            mb: { xs: 0.5, sm: 1 },
          }}
        >
          {week.map((day) => {
            const key = day.format('YYYY-MM-DD');
            const inMonth = day.month() === anchorMonth;
            const isToday = key === todayStr;

            return (
              <Box
                key={key}
                sx={{
                  position: 'relative',
                  display: 'flex',
                  flexDirection: 'column',
                  minHeight: { xs: 108, sm: 124, md: 132 },
                  p: { xs: 0.75, sm: 1 },
                  borderRadius: 2,
                  border: isToday
                    ? `2px solid ${DASHBOARD_ACCENT}`
                    : `1px solid ${surfaceBorder}`,
                  background: defaultCellBg(inMonth),
                  opacity: inMonth ? 1 : 0.42,
                  boxShadow: isToday
                    ? isDark
                      ? `0 0 20px ${alpha(DASHBOARD_ACCENT, 0.25)}`
                      : `0 4px 14px ${alpha(DASHBOARD_ACCENT, 0.35)}`
                    : 'none',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 0.5,
                    minHeight: 26,
                    flexShrink: 0,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.35, minWidth: 0 }}>
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 800,
                        fontSize: '0.82rem',
                        color: dayNumColor(inMonth),
                      }}
                    >
                      {day.format('D')}
                    </Typography>
                    {inMonth && (
                      <Skeleton
                        variant="rounded"
                        animation="wave"
                        width={16}
                        height={16}
                        sx={{ flexShrink: 0, bgcolor: bc }}
                      />
                    )}
                  </Box>
                  {inMonth && (
                    <Skeleton
                      variant="rounded"
                      animation="wave"
                      width={44}
                      height={20}
                      sx={{ flexShrink: 0, borderRadius: 1, bgcolor: bc }}
                    />
                  )}
                </Box>

                {inMonth && (
                  <Box
                    sx={{
                      flex: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      mt: 0.5,
                      minHeight: 0,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.35, flexShrink: 0 }}>
                      <FlightLand sx={{ fontSize: 14, color: muted }} />
                      <Skeleton
                        variant="rounded"
                        animation="wave"
                        height={14}
                        sx={{ flex: 1, maxWidth: 120, bgcolor: bc }}
                      />
                    </Box>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.45,
                        mt: 0.35,
                        flexShrink: 0,
                      }}
                    >
                      <MeetingRoom sx={{ fontSize: 18, color: muted }} />
                      <Skeleton
                        variant="rounded"
                        animation="wave"
                        height={18}
                        sx={{ flex: 1, maxWidth: 100, bgcolor: bc }}
                      />
                    </Box>
                    <Box sx={{ mt: 'auto', pt: 0.75, flexShrink: 0 }}>
                      <Skeleton
                        variant="rounded"
                        animation="wave"
                        height={6}
                        width="100%"
                        sx={{ borderRadius: 3, bgcolor: isDark ? alpha('#fff', 0.1) : alpha('#000', 0.08) }}
                      />
                    </Box>
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>
      ))}
    </Box>
  );
}

/** Neutral stat chip chrome while loading (no vacant/booked/orange colors). */
function statsChipSxSkeleton(isDark) {
  const bd = isDark ? alpha('#fff', 0.2) : alpha('#000', 0.14);
  const fg = isDark ? alpha('#fff', 0.78) : alpha('#000', 0.68);
  return {
    height: STATS_CHIP_H,
    fontSize: '0.72rem',
    fontWeight: 600,
    background: 'transparent',
    color: fg,
    border: `1.5px solid ${bd}`,
    boxShadow: 'none',
    '& .MuiChip-label': { padding: '0 10px' },
  };
}

/**
 * Ultra card grid: matches `BookingCalendarUltraView` "All Rooms" section — header (dot + title),
 * stat chips, responsive card grid; neutral skeleton placeholders only (no status colors).
 */
function UltraCardsCalendarSkeleton({ theme, groupView = 'full', roomRowCount = 6, layoutVariant = 'desktop' }) {
  const isDark = theme === 'dark';
  const isMobileLayout = layoutVariant === 'mobile';
  const isWorkspaceLayout = layoutVariant === 'workspace';
  const safeCount = Math.min(200, Math.max(1, Number(roomRowCount) || 6));
  const sectionTitle = groupView === 'full' ? 'All Rooms' : 'Rooms';
  const bc = boneColor(isDark);
  const cardBg = isDark ? '#2a2a31' : '#ffffff';
  const cardBorder = isDark ? alpha('#fff', 0.1) : alpha('#000', 0.08);

  const renderRoomCard = (idx) => (
    <Paper
      key={idx}
      elevation={0}
      sx={{
        p: isMobileLayout ? 0.65 : 1.75,
        pt: isMobileLayout ? 0.85 : 1.85,
        background: isMobileLayout
          ? 'linear-gradient(165deg, rgba(76,225,182,0.08) 0%, rgba(3,7,18,0.94) 48%, rgba(0,0,0,0.88) 100%)'
          : cardBg,
        border: isMobileLayout ? '1px solid rgba(255,255,255,0.1)' : `1px solid ${cardBorder}`,
        borderRadius: isMobileLayout ? '14px' : '12px',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        alignItems: isMobileLayout ? 'center' : 'flex-start',
        position: 'relative',
        overflow: 'hidden',
        boxShadow: isDark ? '0 2px 8px rgba(0, 0, 0, 0.35)' : '0 1px 4px rgba(0, 0, 0, 0.06)',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: isMobileLayout ? 3 : 2,
          background: isMobileLayout ? alpha(LOGIN_ACCENT, 0.45) : bc,
          zIndex: 1,
        },
      }}
    >
      {isMobileLayout ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
            flex: 1,
            minHeight: 0,
            position: 'relative',
            zIndex: 2,
            pt: 0.25,
          }}
        >
          <Skeleton
            variant="circular"
            animation="wave"
            width={34}
            height={34}
            sx={{
              flexShrink: 0,
              bgcolor: bc,
              border: `1px solid ${alpha(LOGIN_ACCENT, 0.25)}`,
            }}
          />
          <Skeleton
            variant="rounded"
            animation="wave"
            width="38%"
            height={14}
            sx={{ mt: 0.45, bgcolor: bc }}
          />
          <Skeleton
            variant="rounded"
            animation="wave"
            width="48%"
            height={10}
            sx={{ mt: 0.3, borderRadius: 999, bgcolor: bc }}
          />
          <Skeleton
            variant="rounded"
            animation="wave"
            width="55%"
            height={10}
            sx={{ mt: 0.25, bgcolor: bc }}
          />
          <Skeleton
            variant="rounded"
            animation="wave"
            width={36}
            height={16}
            sx={{ mt: 'auto', pt: 0.35, borderRadius: 999, bgcolor: bc }}
          />
        </Box>
      ) : (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'flex-start',
            gap: 1,
            width: '100%',
            flex: 1,
            minWidth: 0,
            position: 'relative',
            zIndex: 2,
            mt: 0.25,
          }}
        >
          <Skeleton
            variant="circular"
            animation="wave"
            width={40}
            height={40}
            sx={{
              flexShrink: 0,
              bgcolor: bc,
              border: `1px solid ${isDark ? alpha('#fff', 0.12) : alpha('#000', 0.08)}`,
            }}
          />
          <Box sx={{ flex: 1, minWidth: 0, pt: 0 }}>
            <Skeleton variant="rounded" animation="wave" width="74%" height={20} sx={{ bgcolor: bc }} />
            <Skeleton
              variant="rounded"
              animation="wave"
              width="52%"
              height={16}
              sx={{ mt: 0.45, borderRadius: 1, bgcolor: bc }}
            />
          </Box>
        </Box>
      )}
    </Paper>
  );

  return (
    <Box className="booking-calendar-container" sx={{ width: '100%' }} aria-busy="true" aria-label="Loading calendar">
      <Paper
        elevation={0}
        sx={
          isMobileLayout
            ? {
                mb: 1.25,
                ...mobileAppGlassCard,
                borderRadius: '16px',
                overflow: 'hidden',
              }
            : {
                mb: 3,
                background: isDark ? 'transparent' : 'linear-gradient(135deg, #ffffff 0%, #fafafa 100%)',
                border: `1px solid ${isDark ? 'rgba(76, 225, 182, 0.15)' : 'rgba(76, 225, 182, 0.1)'}`,
                borderRadius: '16px',
                overflow: 'hidden',
                transition: 'all 0.3s ease',
                boxShadow: isDark ? '0 4px 20px rgba(0, 0, 0, 0.3)' : '0 4px 20px rgba(0, 0, 0, 0.08)',
              }
        }
      >
        <Box
          sx={
            isMobileLayout
              ? {
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.75,
                  p: 1,
                  background: 'rgba(255,255,255,0.03)',
                  borderBottom: '1px solid rgba(255,255,255,0.08)',
                }
              : {
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  p: 1.5,
                  background: isDark ? 'transparent' : 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 50%, #f5f5f5 100%)',
                  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                  position: 'relative',
                  overflow: 'hidden',
                  flexWrap: 'wrap',
                  gap: 1.5,
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '3px',
                    background: isDark
                      ? 'linear-gradient(90deg, rgba(76, 225, 182, 0.6) 0%, rgba(76, 225, 182, 0.4) 25%, rgba(76, 225, 182, 0.3) 50%, rgba(76, 225, 182, 0.4) 75%, rgba(76, 225, 182, 0.6) 100%)'
                      : 'linear-gradient(90deg, rgba(76, 225, 182, 0.7) 0%, rgba(76, 225, 182, 0.5) 25%, rgba(76, 225, 182, 0.4) 50%, rgba(76, 225, 182, 0.5) 75%, rgba(76, 225, 182, 0.7) 100%)',
                    zIndex: 1,
                  },
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: '-100%',
                    width: '100%',
                    height: '100%',
                    background: isDark
                      ? 'linear-gradient(90deg, transparent 0%, rgba(255, 255, 255, 0.05) 50%, transparent 100%)'
                      : 'linear-gradient(90deg, transparent 0%, rgba(255, 255, 255, 0.3) 50%, transparent 100%)',
                    animation: 'shimmer 3s infinite',
                    zIndex: 0,
                    '@keyframes shimmer': {
                      '0%': { left: '-100%' },
                      '100%': { left: '100%' },
                    },
                  },
                }
          }
        >
          {isMobileLayout ? (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.65 }}>
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    bgcolor: LOGIN_ACCENT,
                    boxShadow: `0 0 10px ${alpha(LOGIN_ACCENT, 0.45)}`,
                    flexShrink: 0,
                  }}
                />
                <Skeleton variant="rounded" animation="wave" width={72} height={14} sx={{ bgcolor: boneColor(isDark) }} />
              </Box>
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                {mobilePillSkeleton(isDark, 52)}
                {mobilePillSkeleton(isDark, 58)}
                {mobilePillSkeleton(isDark, 64)}
                {mobilePillSkeleton(isDark, 54)}
                {mobilePillSkeleton(isDark, 60)}
              </Box>
            </>
          ) : (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, ml: 1, position: 'relative', zIndex: 2 }}>
                <Box
                  sx={{
                    width: '10px',
                    height: '10px',
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #4ce1b6 0%, #3ab896 50%, #2a9d7a 100%)',
                    boxShadow:
                      'inset 0 1px 2px rgba(255, 255, 255, 0.3), 0 2px 8px rgba(76, 225, 182, 0.5), 0 1px 4px rgba(76, 225, 182, 0.4)',
                  }}
                />
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    fontSize: '0.95rem',
                    background: isDark
                      ? 'linear-gradient(135deg, #ddd 0%, #b0b0b0 100%)'
                      : 'linear-gradient(135deg, #212121 0%, #424242 100%)',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    letterSpacing: '0.5px',
                    textShadow: isDark ? '0 1px 2px rgba(0, 0, 0, 0.5)' : '0 1px 2px rgba(255, 255, 255, 0.8)',
                  }}
                >
                  {sectionTitle}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', position: 'relative', zIndex: 2 }}>
                <Chip size="small" sx={statsChipSxSkeleton(isDark)} label={ultraStatChipLabel(isDark, 14, 'Total')} />
                <Chip size="small" sx={statsChipSxSkeleton(isDark)} label={ultraStatChipLabel(isDark, 14, 'Vacant')} />
                <Chip size="small" sx={statsChipSxSkeleton(isDark)} label={ultraStatChipLabel(isDark, 14, 'Occupied')} />
                <Chip size="small" sx={statsChipSxSkeleton(isDark)} label={ultraStatChipLabel(isDark, 14, 'Booked')} />
                <Chip size="small" sx={statsChipSxSkeleton(isDark)} label={ultraStatChipLabel(isDark, 14, 'Blocked')} />
              </Box>
            </>
          )}
        </Box>

        <Box sx={getUltraRoomGridContainerSx({ isDark, isMobileLayout, isWorkspaceLayout })}>
          {Array.from({ length: safeCount }).map((_, idx) => renderRoomCard(idx))}
        </Box>
      </Paper>
    </Box>
  );
}

function statChipPlaceholder(isDark, width) {
  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        height: '22px',
        px: 1,
        borderRadius: 999,
        border: `1px solid ${isDark ? 'rgba(76, 225, 182, 0.35)' : 'rgba(0, 0, 0, 0.15)'}`,
        backgroundColor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
      }}
    >
      <Skeleton variant="rounded" animation="wave" width={width} height={14} sx={{ bgcolor: boneColor(isDark) }} />
    </Box>
  );
}

/**
 * Mobile week grid skeleton — matches compact `BookingCalendarGridView` (7 equal day cols, 44px room col).
 */
function MobileWeekGridCalendarSkeleton({ isDark, dateRange, safeRowCount }) {
  const bc = boneColor(isDark);
  const dayCount = dateRange.length;
  const gridCols = `${MOBILE_ROOM_LABEL_WIDTH}px repeat(${dayCount}, minmax(0, 1fr))`;
  const rowMinHeight = 36;
  const cellBorder = '1px solid rgba(255,255,255,0.06)';

  return (
    <Box
      className="booking-calendar-container"
      sx={{ width: '100%', overflowX: 'hidden' }}
      aria-busy="true"
      aria-label="Loading calendar"
    >
      <Paper
        elevation={0}
        sx={{
          mb: 1.25,
          ...mobileAppGlassCard,
          borderRadius: '16px',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            p: 1,
            background: 'rgba(255,255,255,0.03)',
            borderBottom: cellBorder,
            display: 'flex',
            flexDirection: 'column',
            gap: 0.65,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 0.75 }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Skeleton variant="rounded" animation="wave" width={88} height={14} sx={{ bgcolor: bc }} />
              <Skeleton
                variant="rounded"
                animation="wave"
                width={132}
                height={10}
                sx={{ mt: 0.35, bgcolor: bc }}
              />
            </Box>
            {mobilePillSkeleton(isDark, 56, 22)}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
            <Skeleton variant="rounded" animation="wave" width={64} height={10} sx={{ bgcolor: bc }} />
            {mobilePillSkeleton(isDark, 48, 20)}
            {mobilePillSkeleton(isDark, 72, 20)}
            {mobilePillSkeleton(isDark, 80, 20)}
          </Box>
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: gridCols,
            bgcolor: 'rgba(0,0,0,0.28)',
            borderBottom: cellBorder,
          }}
        >
          <Box
            sx={{
              py: 0.35,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              borderRight: cellBorder,
            }}
          >
            <Skeleton variant="rounded" animation="wave" width={16} height={8} sx={{ bgcolor: bc }} />
            <Skeleton variant="rounded" animation="wave" width={14} height={14} sx={{ mt: 0.25, bgcolor: bc }} />
          </Box>
          {dateRange.map((dateInfo, index) => (
            <Box
              key={dateInfo.key}
              sx={{
                py: 0.35,
                px: 0.15,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 0.1,
                borderRight: index < dayCount - 1 ? cellBorder : 'none',
              }}
            >
              <Skeleton variant="rounded" animation="wave" width={8} height={8} sx={{ bgcolor: bc }} />
              <Skeleton variant="rounded" animation="wave" width={12} height={12} sx={{ bgcolor: bc }} />
            </Box>
          ))}
        </Box>

        <Box sx={{ position: 'relative' }}>
          {Array.from({ length: safeRowCount }).map((_, roomIndex) => (
            <Box
              key={roomIndex}
              sx={{
                display: 'grid',
                gridTemplateColumns: gridCols,
                minHeight: `${rowMinHeight}px`,
                borderBottom: roomIndex < safeRowCount - 1 ? '1px solid rgba(255,255,255,0.05)' : 'none',
              }}
            >
              <Box
                sx={{
                  p: 0.35,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRight: cellBorder,
                  bgcolor: 'rgba(255,255,255,0.02)',
                }}
              >
                <Skeleton
                  variant="rounded"
                  animation="wave"
                  width={28}
                  height={18}
                  sx={{ borderRadius: '8px', bgcolor: alpha(LOGIN_ACCENT, 0.18) }}
                />
                <Skeleton
                  variant="rounded"
                  animation="wave"
                  width={22}
                  height={8}
                  sx={{ mt: 0.15, borderRadius: '4px', bgcolor: bc }}
                />
              </Box>
              <Box
                sx={{
                  gridColumn: `2 / span ${dayCount}`,
                  display: 'grid',
                  gridTemplateColumns: `repeat(${dayCount}, minmax(0, 1fr))`,
                  minHeight: `${rowMinHeight}px`,
                }}
              >
                {dateRange.map((dateInfo, colIndex) => (
                  <Box
                    key={dateInfo.key}
                    sx={{
                      minWidth: 0,
                      borderRight: colIndex < dayCount - 1 ? '1px solid rgba(255,255,255,0.05)' : 'none',
                      bgcolor:
                        dateInfo.day === 'Sat' || dateInfo.day === 'Sun'
                          ? 'rgba(255,255,255,0.025)'
                          : '#1e1e24',
                    }}
                  />
                ))}
              </Box>
            </Box>
          ))}
        </Box>
      </Paper>
    </Box>
  );
}

/**
 * Week timeline grid: matches `BookingCalendarGridView` (All Rooms, room count chip,
 * Bars this week + summary chips, DateHeader row, ROOMS column + 7 day columns).
 * Grid cells stay empty (no fake booking bars); bars render after data loads.
 * Room rows = `roomRowCount` (from last successful load).
 */
function WeekGridCalendarSkeleton({
  theme,
  anchorDate,
  groupView = 'full',
  roomRowCount = 6,
  layoutVariant = 'desktop',
  gridDayCount = CALENDAR_GRID_DAY_COUNT,
}) {
  const isDark = theme === 'dark';
  const isMobileLayout = layoutVariant === 'mobile';
  const currentDateStr = getTodayDateString();

  const dateRange = useMemo(() => {
    const start = moment(anchorDate || new Date()).startOf('day');
    const end = start.clone().add(gridDayCount - 1, 'days');
    return generateDateRange(start.format('YYYY-MM-DD'), end.format('YYYY-MM-DD'));
  }, [anchorDate, gridDayCount]);

  const sectionTitle = groupView === 'full' ? 'All Rooms' : 'Rooms';
  const safeRowCount = Math.min(200, Math.max(1, Number(roomRowCount) || 6));

  if (isMobileLayout) {
    return (
      <MobileWeekGridCalendarSkeleton
        isDark={isDark}
        dateRange={dateRange}
        safeRowCount={safeRowCount}
      />
    );
  }

  return (
    <Box className="booking-calendar-container" sx={{ width: '100%', overflowX: 'auto' }} aria-busy="true" aria-label="Loading calendar">
      <Paper
        elevation={isDark ? 8 : 2}
        sx={{
          mb: 3,
          background: isDark
            ? 'linear-gradient(135deg, #2a2a31 0%, #1e1e24 100%)'
            : 'linear-gradient(135deg, #ffffff 0%, #fafafa 100%)',
          border: `1px solid ${isDark ? 'rgba(76, 225, 182, 0.2)' : 'rgba(0, 0, 0, 0.12)'}`,
          borderRadius: '16px',
          overflow: 'visible',
          position: 'relative',
          boxShadow: isDark
            ? '0 8px 32px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(76, 225, 182, 0.1)'
            : '0 4px 20px rgba(0, 0, 0, 0.08), 0 0 0 1px rgba(0, 0, 0, 0.05)',
        }}
      >
        <Box
          sx={{
            p: 1.5,
            background: isDark
              ? 'linear-gradient(135deg, #2a2a31 0%, #1e1e24 50%, #1a1a20 100%)'
              : 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 50%, #f5f5f5 100%)',
            borderBottom: `1px solid ${isDark ? 'rgba(76, 225, 182, 0.15)' : 'rgba(0, 0, 0, 0.08)'}`,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'stretch',
            gap: 1,
            position: 'relative',
            overflow: 'hidden',
            boxShadow: isDark
              ? 'inset 0 2px 4px rgba(255, 255, 255, 0.08), inset 0 -2px 4px rgba(0, 0, 0, 0.4), 0 4px 16px rgba(0, 0, 0, 0.5), 0 2px 8px rgba(0, 0, 0, 0.4), 0 1px 4px rgba(0, 0, 0, 0.3)'
              : 'inset 0 2px 4px rgba(255, 255, 255, 0.95), inset 0 -2px 4px rgba(0, 0, 0, 0.08), 0 4px 16px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.08), 0 1px 4px rgba(0, 0, 0, 0.05)',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '3px',
              background: isDark
                ? 'linear-gradient(90deg, rgba(76, 225, 182, 0.6) 0%, rgba(76, 225, 182, 0.4) 25%, rgba(76, 225, 182, 0.3) 50%, rgba(76, 225, 182, 0.4) 75%, rgba(76, 225, 182, 0.6) 100%)'
                : 'linear-gradient(90deg, rgba(76, 225, 182, 0.7) 0%, rgba(76, 225, 182, 0.5) 25%, rgba(76, 225, 182, 0.4) 50%, rgba(76, 225, 182, 0.5) 75%, rgba(76, 225, 182, 0.7) 100%)',
              boxShadow: '0 2px 8px rgba(76, 225, 182, 0.4), 0 1px 4px rgba(76, 225, 182, 0.3)',
              zIndex: 1,
            },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              position: 'relative',
              zIndex: 2,
            }}
          >
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: '0.85rem',
                color: isDark ? '#fff' : '#212121',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                textShadow: isDark
                  ? '0 1px 2px rgba(0, 0, 0, 0.5), 0 1px 1px rgba(255, 255, 255, 0.1)'
                  : '0 1px 2px rgba(255, 255, 255, 0.8), 0 1px 1px rgba(0, 0, 0, 0.1)',
              }}
            >
              {sectionTitle}
            </Typography>
            <Chip
              label={`${safeRowCount} ${safeRowCount === 1 ? 'Room' : 'Rooms'}`}
              size="small"
              sx={{
                height: '22px',
                fontSize: '0.7rem',
                fontWeight: 600,
                backgroundColor: isDark ? 'rgba(76, 225, 182, 0.2)' : 'rgba(76, 225, 182, 0.15)',
                color: isDark ? '#4ce1b6' : '#00a085',
                border: `1px solid ${isDark ? 'rgba(76, 225, 182, 0.4)' : 'rgba(76, 225, 182, 0.3)'}`,
                boxShadow: isDark
                  ? 'inset 0 1px 2px rgba(255, 255, 255, 0.1), 0 1px 3px rgba(76, 225, 182, 0.3)'
                  : 'inset 0 1px 2px rgba(255, 255, 255, 0.5), 0 1px 2px rgba(76, 225, 182, 0.2)',
                '& .MuiChip-label': {
                  padding: '0 8px',
                },
              }}
            />
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: 0.75,
              position: 'relative',
              zIndex: 2,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: isDark ? 'rgba(255,255,255,0.55)' : 'rgba(0,0,0,0.5)',
                fontWeight: 600,
                textTransform: 'uppercase',
                fontSize: '0.65rem',
                letterSpacing: '0.04em',
              }}
            >
              Bars this week
            </Typography>
            {statChipPlaceholder(isDark, 72)}
            {statChipPlaceholder(isDark, 96)}
            {statChipPlaceholder(isDark, 108)}
          </Box>
        </Box>

        <Box
          sx={{
            display: 'flex',
            borderBottom: `2px solid ${isDark ? 'rgba(76, 225, 182, 0.2)' : 'rgba(0, 0, 0, 0.1)'}`,
            position: 'relative',
            overflow: 'hidden',
            background: isDark
              ? 'linear-gradient(180deg, #2a2a31 0%, #1e1e24 50%, #1a1a20 100%)'
              : 'linear-gradient(180deg, #ffffff 0%, #f8f9fa 50%, #f5f5f5 100%)',
            boxShadow: isDark
              ? 'inset 0 2px 4px rgba(255, 255, 255, 0.08), inset 0 -2px 4px rgba(0, 0, 0, 0.4), 0 4px 16px rgba(0, 0, 0, 0.5), 0 2px 8px rgba(0, 0, 0, 0.4), 0 1px 4px rgba(0, 0, 0, 0.3)'
              : 'inset 0 2px 4px rgba(255, 255, 255, 0.95), inset 0 -2px 4px rgba(0, 0, 0, 0.08), 0 4px 16px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.08), 0 1px 4px rgba(0, 0, 0, 0.05)',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '3px',
              background: isDark
                ? 'linear-gradient(90deg, rgba(76, 225, 182, 0.6) 0%, rgba(76, 225, 182, 0.4) 25%, rgba(76, 225, 182, 0.3) 50%, rgba(76, 225, 182, 0.4) 75%, rgba(76, 225, 182, 0.6) 100%)'
                : 'linear-gradient(90deg, rgba(76, 225, 182, 0.7) 0%, rgba(76, 225, 182, 0.5) 25%, rgba(76, 225, 182, 0.4) 50%, rgba(76, 225, 182, 0.5) 75%, rgba(76, 225, 182, 0.7) 100%)',
              boxShadow: '0 2px 8px rgba(76, 225, 182, 0.4), 0 1px 4px rgba(76, 225, 182, 0.3)',
              zIndex: 1,
            },
          }}
        >
          <Box
            sx={{
              width: '120px',
              minWidth: '120px',
              p: 1.5,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              background: isDark
                ? 'linear-gradient(135deg, #2a2a31 0%, #1e1e24 100%)'
                : 'linear-gradient(135deg, #f5f5f5 0%, #ffffff 100%)',
              borderRight: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)'}`,
              position: 'relative',
              boxShadow: isDark
                ? 'inset 0 1px 2px rgba(255, 255, 255, 0.05), 0 1px 2px rgba(0, 0, 0, 0.3)'
                : 'inset 0 1px 2px rgba(255, 255, 255, 0.8), 0 1px 2px rgba(0, 0, 0, 0.1)',
              zIndex: 2,
            }}
          >
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: '0.7rem',
                color: isDark ? '#ddd' : '#212121',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                lineHeight: 1.2,
                textShadow: isDark ? '0 1px 2px rgba(0, 0, 0, 0.5)' : '0 1px 1px rgba(255, 255, 255, 0.8)',
              }}
            >
              Rooms
            </Typography>
            <Chip
              label={`${safeRowCount}`}
              size="small"
              sx={{
                height: '18px',
                mt: 0.4,
                fontSize: '0.65rem',
                fontWeight: 600,
                backgroundColor: isDark ? 'rgba(76, 225, 182, 0.2)' : 'rgba(76, 225, 182, 0.15)',
                color: isDark ? '#4ce1b6' : '#00a085',
                border: `1px solid ${isDark ? 'rgba(76, 225, 182, 0.4)' : 'rgba(76, 225, 182, 0.3)'}`,
                boxShadow: isDark
                  ? 'inset 0 1px 2px rgba(255, 255, 255, 0.1), 0 1px 2px rgba(76, 225, 182, 0.3)'
                  : 'inset 0 1px 2px rgba(255, 255, 255, 0.5), 0 1px 2px rgba(76, 225, 182, 0.2)',
                '& .MuiChip-label': {
                  padding: '0 5px',
                },
              }}
            />
          </Box>

          {dateRange.map((dateInfo, index) => (
            <DateHeader
              key={dateInfo.key}
              dateInfo={dateInfo}
              isToday={dateInfo.key === currentDateStr}
              isDarkMode={isDark}
              isLast={index === dateRange.length - 1}
            />
          ))}
        </Box>

        <Box sx={{ position: 'relative' }}>
          {Array.from({ length: safeRowCount }).map((_, roomIndex) => {
            const rowMinHeight = Math.max(56, 8 + 44 + 8);

            return (
              <Box
                key={roomIndex}
                sx={{
                  display: 'flex',
                  borderBottom:
                    roomIndex < safeRowCount - 1
                      ? `1px solid ${isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)'}`
                      : 'none',
                  minHeight: `${rowMinHeight}px`,
                  position: 'relative',
                  overflow: 'visible',
                }}
              >
                <Box
                  sx={{
                    width: '120px',
                    minWidth: '120px',
                    p: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    background: isDark ? '#2a2a31' : '#ffffff',
                    borderRight: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                    <Skeleton
                      variant="rounded"
                      animation="wave"
                      width={44}
                      height={24}
                      sx={{
                        borderRadius: '12px',
                        bgcolor: isDark ? 'rgba(76, 225, 182, 0.15)' : 'rgba(76, 225, 182, 0.1)',
                      }}
                    />
                    <Skeleton
                      variant="rounded"
                      animation="wave"
                      width={36}
                      height={20}
                      sx={{
                        borderRadius: '10px',
                        bgcolor: isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)',
                      }}
                    />
                  </Box>
                </Box>

                <Box
                  sx={{
                    flex: 1,
                    display: 'flex',
                    position: 'relative',
                    minHeight: `${Math.max(48, rowMinHeight)}px`,
                    overflow: 'visible',
                  }}
                >
                  {dateRange.map((dateInfo, colIndex) => (
                    <Box
                      key={dateInfo.key}
                      sx={{
                        flex: 1,
                        minWidth: '100px',
                        borderRight:
                          colIndex < dateRange.length - 1
                            ? `1px solid ${isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)'}`
                            : 'none',
                        position: 'relative',
                        background: isDark ? '#1e1e24' : '#fafafa',
                      }}
                    />
                  ))}
                </Box>
              </Box>
            );
          })}
        </Box>
      </Paper>
    </Box>
  );
}

/**
 * Loading placeholder: month overview; ultra = card grid (`BookingCalendarUltraView`);
 * calendar / full / room = week timeline (`BookingCalendarGridView`).
 */
export default function BookingCalendarSkeleton({
  theme = 'light',
  displayView = 'ultra',
  anchorDate,
  groupView = 'full',
  roomRowCount,
  layoutVariant = 'desktop',
  gridDayCount = CALENDAR_GRID_DAY_COUNT,
}) {
  const rows =
    roomRowCount != null && Number.isFinite(Number(roomRowCount))
      ? Number(roomRowCount)
      : readStoredCalendarRoomRowCount();

  if (displayView === 'month') {
    return <MonthCalendarSkeleton theme={theme} anchorDate={anchorDate} />;
  }
  if (displayView === 'ultra') {
    return (
      <UltraCardsCalendarSkeleton theme={theme} groupView={groupView} roomRowCount={rows} layoutVariant={layoutVariant} />
    );
  }
  return (
    <WeekGridCalendarSkeleton
      theme={theme}
      anchorDate={anchorDate}
      groupView={groupView}
      roomRowCount={rows}
      layoutVariant={layoutVariant}
      gridDayCount={gridDayCount}
    />
  );
}
